import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card, CardContent, Typography, Grid, Box, Button, TextField,
    Switch, FormControlLabel, Alert, CircularProgress, Breadcrumbs, Link
} from '@mui/material';
import { useNotify } from 'react-admin';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import ClearIcon from '@mui/icons-material/Clear';
import InfoIcon from '@mui/icons-material/Info';
import GroupsIcon from '@mui/icons-material/Groups';
import { motion } from 'framer-motion';
import axios from 'axios';
import config from '../../config/config.json';

const host = config.production.host;
const getJwt = () => localStorage.getItem("feathers-jwt");

export const GruppiCreatePage = () => {
    const navigate = useNavigate();
    const notify = useNotify();

    const [formData, setFormData] = React.useState({
        nome: '',
        note: '',
        flVisibile: true,
        flAttivo: true,
        ordinamento: ''
    });

    const [saving, setSaving] = React.useState(false);
    const [errors, setErrors] = React.useState({});
    const [touched, setTouched] = React.useState({});

    const validateField = (name, value) => {
        const newErrors = { ...errors };

        switch (name) {
            case 'nome':
                if (!value.trim()) {
                    newErrors.nome = 'Il nome del gruppo è obbligatorio';
                } else if (value.trim().length < 2) {
                    newErrors.nome = 'Il nome deve essere di almeno 2 caratteri';
                } else {
                    delete newErrors.nome;
                }
                break;

            case 'ordinamento':
                if (value && (isNaN(value) || value < 0)) {
                    newErrors.ordinamento = 'L\'ordinamento deve essere un numero positivo';
                } else {
                    delete newErrors.ordinamento;
                }
                break;

            default:
                delete newErrors[name];
                break;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleFieldChange = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
        setTouched(prev => ({ ...prev, [name]: true }));
        validateField(name, value);
    };

    const validateForm = () => {
        const fieldsToValidate = ['nome', 'ordinamento'];
        let isValid = true;

        fieldsToValidate.forEach(field => {
            if (!validateField(field, formData[field])) {
                isValid = false;
            }
            setTouched(prev => ({ ...prev, [field]: true }));
        });

        return isValid;
    };

    const handleSave = async () => {
        if (!validateForm()) {
            notify("Correggere gli errori nel form", { type: 'error' });
            return;
        }

        try {
            setSaving(true);
            const jwt = getJwt();

            const dataToSend = {
                nome: formData.nome.trim(),
                note: formData.note.trim() || null,
                flVisibile: formData.flVisibile ? 1 : 0,
                flAttivo: formData.flAttivo ? 1 : 0,
                flAnnullato: 0,
                ordinamento: formData.ordinamento ? parseInt(formData.ordinamento) : 0
            };

            await axios.post(`${host}/tbl-gruppo`, dataToSend, {
                headers: { 'Authorization': `Bearer ${jwt}` }
            });

            notify(`Gruppo "${formData.nome}" creato con successo`, { type: 'success' });
            navigate('/gruppi');

        } catch (error) {
            console.error("Errore creazione gruppo:", error);
            notify(error.response?.data?.message || "Errore nella creazione del gruppo", { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleReset = () => {
        setFormData({ nome: '', note: '', flVisibile: true, flAttivo: true, ordinamento: '' });
        setErrors({});
        setTouched({});
    };

    const hasErrors = Object.keys(errors).length > 0;
    const canSave = formData.nome.trim() && !hasErrors;

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f6f7fb', minHeight: '100vh' }}>
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>

                <Breadcrumbs sx={{ mb: 3 }}>
                    <Link component="button" variant="body2" onClick={() => navigate('/gruppi')} sx={{ textDecoration: 'none' }}>
                        Gruppi
                    </Link>
                    <Typography variant="body2" color="text.primary">Nuovo Gruppo</Typography>
                </Breadcrumbs>

                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                            <GroupsIcon sx={{ fontSize: 32, verticalAlign: 'middle', mr: 1 }} />
                            Crea Nuovo Gruppo
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Inserisci i dettagli per creare un nuovo gruppo
                        </Typography>
                    </Box>
                    <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/gruppi')}>
                        Indietro
                    </Button>
                </Box>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={8}>
                        <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                                    <InfoIcon color="primary" />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Informazioni Gruppo</Typography>
                                </Box>

                                <Grid container spacing={3}>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Nome Gruppo *"
                                            value={formData.nome}
                                            onChange={(e) => handleFieldChange('nome', e.target.value)}
                                            error={touched.nome && !!errors.nome}
                                            helperText={touched.nome && errors.nome}
                                            placeholder="Inserisci il nome del gruppo"
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Ordinamento"
                                            type="number"
                                            value={formData.ordinamento}
                                            onChange={(e) => handleFieldChange('ordinamento', e.target.value)}
                                            error={touched.ordinamento && !!errors.ordinamento}
                                            helperText={touched.ordinamento && errors.ordinamento || "Numero per ordinare i gruppi"}
                                            placeholder="0"
                                            inputProps={{ min: 0, step: 1 }}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Note"
                                            multiline
                                            rows={4}
                                            value={formData.note}
                                            onChange={(e) => handleFieldChange('note', e.target.value)}
                                            placeholder="Note aggiuntive sul gruppo..."
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Card sx={{ borderRadius: 3, boxShadow: 2, mb: 3 }}>
                            <CardContent sx={{ p: 3, textAlign: 'center' }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Anteprima Gruppo</Typography>
                                <GroupsIcon sx={{ fontSize: 60, color: '#1976d2', mb: 2 }} />
                                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                                    {formData.nome || 'Nome Gruppo'}
                                </Typography>
                                {formData.note && (
                                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mt: 2 }}>
                                        {formData.note.length > 80 ? `${formData.note.substring(0, 80)}...` : formData.note}
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>

                        <Card sx={{ borderRadius: 3, boxShadow: 2, mb: 3 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Impostazioni</Typography>
                                <FormControlLabel
                                    control={<Switch checked={formData.flVisibile} onChange={(e) => handleFieldChange('flVisibile', e.target.checked)} color="primary" />}
                                    label="Gruppo visibile"
                                    sx={{ mb: 1 }}
                                />
                                <FormControlLabel
                                    control={<Switch checked={formData.flAttivo} onChange={(e) => handleFieldChange('flAttivo', e.target.checked)} color="primary" />}
                                    label="Gruppo attivo"
                                />
                            </CardContent>
                        </Card>

                        {hasErrors && (
                            <Alert severity="error" sx={{ mb: 3 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Correggere i seguenti errori:</Typography>
                                <ul style={{ margin: 0, paddingLeft: 16 }}>
                                    {Object.values(errors).map((error, index) => (
                                        <li key={index}><Typography variant="body2">{error}</Typography></li>
                                    ))}
                                </ul>
                            </Alert>
                        )}

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                onClick={handleSave}
                                disabled={!canSave || saving}
                                sx={{ py: 1.5 }}
                            >
                                {saving ? 'Salvataggio...' : 'Crea Gruppo'}
                            </Button>
                            <Button variant="outlined" startIcon={<ClearIcon />} onClick={handleReset} disabled={saving}>
                                Reset Form
                            </Button>
                        </Box>
                    </Grid>
                </Grid>

                <Box sx={{ mt: 4, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">* Campi obbligatori</Typography>
                </Box>

            </motion.div>
        </Box>
    );
};
